import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Typography,
  Alert
} from '@mui/material';
import AuthenticateService from './AuthenticateService';
import Sidebar from './Sidebar';
import Header from './Header';
import PrintDialog from './eductionalPrintDialog';
import Footer from './Footer';

const AppUrl = {
  otherBillsEducationPurchase: 'users/other-bills/eduction/purchase',
  otherBillsGetVariation: 'users/other-bills/getvariation'
};

const EducationServiceScreen = () => {
  const { http, user } = AuthenticateService();
  const { token } = AuthenticateService();

  // Account selection state
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [selectedAccountIndex, setSelectedAccountIndex] = useState(0);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);

  // Service types with icons and display names
  const serviceTypes = [
    {
      code: 'waec-registration',
      name: 'WAEC Registration',
      icon: 'school',
      image: 'https://core.agritechbanking.com/public/assets/education/waec.png',
    },
    {
      code: 'waec',
      name: 'WAEC Result',
      icon: 'assignment_turned_in',
      image: 'https://core.agritechbanking.com/public/assets/education/waec.png',
    },
    {
      code: 'jamb',
      name: 'JAMB',
      icon: 'edit_document',
      image: 'https://core.agritechbanking.com/public/assets/education/jamb.png',
    },
  ];

  const [selectedService, setSelectedService] = useState(null);
  const [selectedVariation, setSelectedVariation] = useState(null);
  const [variations, setVariations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [variationsLoading, setVariationsLoading] = useState(false);
  const [phone, setPhone] = useState('');
  const [pin, setPin] = useState('');
  const [billersCode, setBillersCode] = useState('');
  const [showBillersCodeField, setShowBillersCodeField] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [successDialog, setSuccessDialog] = useState({ open: false, data: null });

  // API configuration with Authorization header
  const apiConfig = {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  };

  useEffect(() => {
    getUserAccounts();
  }, []);

  useEffect(() => {
    if (selectedService) {
      fetchVariations();
    }
  }, [selectedService]);

  // Fetch user accounts
  const getUserAccounts = async () => {
    try {
      setIsLoadingAccounts(true);
      const response = await http.get('/users/get-details', apiConfig);

      if (response.data.status === true || response.data.status === 1) {
        const accountsData = response.data.data?.accounts || response.data.message?.data?.accounts || [];
        setAccounts(accountsData);

        if (accountsData.length > 0) {
          setSelectedAccountId(accountsData[0].id);
          setSelectedAccountIndex(0);
        }
      }
    } catch (error) {
      console.error('Error fetching accounts:', error);
      setSnackbar({ open: true, message: 'Failed to load accounts' });
    } finally {
      setIsLoadingAccounts(false);
    }
  };

  // Handle account selection
  const handleAccountSelect = (index, account) => {
    setSelectedAccountIndex(index);
    setSelectedAccountId(account.id);
  };

  // Accounts Ribbon Component
  const AccountsRibbon = () => {
    if (isLoadingAccounts) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={24} />
          <Typography variant="body2" sx={{ ml: 2 }}>
            Loading accounts...
          </Typography>
        </Box>
      );
    }

    if (accounts.length === 0) {
      return (
        <Alert severity="warning" sx={{ mb: 3 }}>
          No accounts found. Please add an account first.
        </Alert>
      );
    }

    return (
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Select Source Account
        </Typography>
        <Box sx={{ display: 'flex', overflowX: 'auto', gap: 2, pb: 2 }}>
          {accounts.map((account, index) => (
            <Card
              key={account.id}
              onClick={() => handleAccountSelect(index, account)}
              sx={{
                minWidth: 220,
                cursor: 'pointer',
                backgroundColor: selectedAccountIndex === index ? 'primary.main' : 'background.paper',
                color: selectedAccountIndex === index ? 'primary.contrastText' : 'text.primary',
                border: selectedAccountIndex === index ? '2px solid' : '1px solid',
                borderColor: selectedAccountIndex === index ? 'primary.main' : 'divider',
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 3,
                }
              }}
            >
              <CardContent>
                <Typography variant="body2" sx={{
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  {account.account_type?.savings_product || 'Account'}
                </Typography>
                <Typography variant="h6" sx={{
                  fontWeight: 'bold',
                  mt: 1,
                  color: selectedAccountIndex === index ? 'primary.contrastText' : 'text.primary'
                }}>
                  ₦{account.user_wallet?.balance || '0.00'}
                </Typography>
                <Typography variant="caption" sx={{
                  display: 'block',
                  mt: 1,
                  color: selectedAccountIndex === index ? 'rgba(255,255,255,0.8)' : 'text.secondary'
                }}>
                  A/C: {account.virtual_accnum || 'N/A'}
                </Typography>
                <Typography variant="caption" sx={{
                  display: 'block',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  color: selectedAccountIndex === index ? 'rgba(255,255,255,0.7)' : 'text.secondary'
                }}>
                  {account.bank?.bank_name || 'Bank'}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Box>
    );
  };

  const fetchVariations = async () => {
    setVariationsLoading(true);
    setVariations([]);
    setSelectedVariation(null);

    try {
      const response = await http.get(
        `${AppUrl.otherBillsGetVariation}?serviceID=${selectedService}`,
        apiConfig
      );

      if (response.data.status === true) {
        setVariations(response.data.data || []);
      } else {
        console.log(response.data.message);
        setSnackbar({ open: true, message: response.data.message || 'Failed to load variations' });
      }
    } catch (e) {
      setSnackbar({ open: true, message: `Error fetching variations: ${e.toString()}` });
      console.error(e.toString());
    } finally {
      setVariationsLoading(false);
    }
  };

  const submitPurchase = async () => {
    if (!selectedService || !selectedVariation || !phone || !pin) {
      setSnackbar({ open: true, message: 'Please fill all fields' });
      return;
    }

    if (!selectedAccountId) {
      setSnackbar({ open: true, message: 'Please select a source account' });
      return;
    }

    setIsLoading(true);

    try {
      const payload = {
        serviceID: selectedService,
        variation_code: selectedVariation,
        phone: phone,
        transaction_pin: pin,
        selectedAccountId: selectedAccountId
      };

      if (showBillersCodeField) {
        payload.billersCode = billersCode;
      }

      const response = await http.post(
        `${AppUrl.otherBillsEducationPurchase}`,
        payload,
        apiConfig
      );

      if (response.data.status === true) {
        handleSuccessResponse(response.data.data.data);
      } else {
        setSnackbar({
          open: true,
          message: response.data?.message || 'Purchase failed'
        });
      }
    } catch (e) {
      setSnackbar({ open: true, message: `Error: ${e.toString()}` });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuccessResponse = (response) => {
    const transactionData = extractNestedData(response, ['data']);
    const verificationData = extractNestedData(response, ['data', 'content', 'transactions']);
    const cards = extractNestedData(response, ['data', 'cards']);

    let message = 'Transaction successful';
    let details = {};

    if (verificationData) {
      details.product_name = verificationData.product_name || 'N/A';
      details.amount = verificationData.amount || 'N/A';
      details.status = verificationData.status || 'N/A';
      details.transactionId = verificationData.transactionId || 'N/A';
    }

    if (transactionData?.purchased_code) {
      details.purchased_code = transactionData.purchased_code;
    }
    if (transactionData?.Pin) {
      details.Pin = transactionData.Pin;
    }
    if (transactionData?.requestId) {
      details.requestId = transactionData.requestId;
    }

    setSuccessDialog({
      open: true,
      data: {
        message,
        details,
        cards
      }
    });
  };

  const handleServiceSelect = (serviceCode) => {
    setSelectedService(serviceCode);
    setSelectedVariation(null);
    setVariations([]);
    setShowBillersCodeField(serviceCode === 'jamb');
    if (serviceCode !== 'jamb') {
      setBillersCode('');
    }
  };

  const extractNestedData = (data, keys) => {
    let result = data;
    for (const key of keys) {
      if (result && typeof result === 'object' && key in result) {
        result = result[key];
      } else {
        return null;
      }
    }
    return result;
  };

  const ServiceTypeCard = ({ service }) => (
    <Card
      elevation={4}
      sx={{
        width: 120,
        border: selectedService === service.code ? '2px solid primary.main' : 'none',
        cursor: 'pointer',
        transition: 'transform 0.2s',
        '&:hover': {
          transform: 'translateY(-2px)'
        }
      }}
      onClick={() => handleServiceSelect(service.code)}
    >
      <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <img
          src={service.image}
          alt={service.name}
          style={{ width: 40, height: 40, objectFit: 'contain' }}
        />
        <Typography
          variant="body1"
          textAlign="center"
          fontWeight={selectedService === service.code ? 'bold' : 'normal'}
          mt={1}
        >
          {service.name}
        </Typography>
      </CardContent>
    </Card>
  );

  return (
    <>
      <Sidebar />
      <Header user={user} />
      <div className="content bg-light">
        <Box sx={{ maxWidth: 1200, margin: '0 auto' }}>
          <AppBar position="static" sx={{ mb: 3 }}>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1, p: 2 }}>
              Education Services
            </Typography>
          </AppBar>

          <Box sx={{ p: 3 }}>
            {/* Accounts Ribbon */}
            <AccountsRibbon />

            <Typography variant="h6" gutterBottom>
              Select Service
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', py: 2, mb: 3 }}>
              {serviceTypes.map((service) => (
                <ServiceTypeCard key={service.code} service={service} />
              ))}
            </Box>

            {variationsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ ml: 2 }}>
                  Loading variations...
                </Typography>
              </Box>
            ) : variations.length > 0 && (
              <Box sx={{ my: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>{selectedService === 'jamb' ? 'Select UTME/DE' : 'Select Option'}</InputLabel>
                  <Select
                    value={selectedVariation || ''}
                    label={selectedService === 'jamb' ? 'Select UTME/DE' : 'Select Option'}
                    onChange={(e) => setSelectedVariation(e.target.value)}
                    sx={{ mb: 2 }}
                  >
                    {variations.map((variation) => (
                      <MenuItem key={variation.variation_code} value={variation.variation_code}>
                        {`${variation.name} - ₦${variation.variation_amount}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {selectedVariation && (
                  <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                    Amount: ₦{variations.find(v => v.variation_code === selectedVariation)?.variation_amount}
                  </Typography>
                )}
              </Box>
            )}

            <TextField
              fullWidth
              label="Phone Number"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              margin="normal"
              type="tel"
              required
            />

            {showBillersCodeField && (
              <TextField
                fullWidth
                label="JAMB profile code"
                value={billersCode}
                onChange={(e) => setBillersCode(e.target.value)}
                margin="normal"
                type="number"
                helperText="Enter your JAMB profile code"
                required={selectedService === 'jamb'}
              />
            )}

            <TextField
              fullWidth
              label="Transaction PIN"
              value={pin}
              onChange={(e) => {
                const value = e.target.value;
                if (/^\d{0,4}$/.test(value)) {
                  setPin(value);
                }
              }}
              margin="normal"
              type="password"
              required
            />

            <Button
              variant="contained"
              onClick={submitPurchase}
              disabled={isLoading || !selectedAccountId}
              sx={{ mt: 3 }}
              startIcon={isLoading ? <CircularProgress size={20} /> : null}
              size="large"
            >
              {isLoading ? 'Processing...' : 'Purchase'}
            </Button>
          </Box>

          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            message={snackbar.message}
          />
          <PrintDialog successDialog={successDialog} setSuccessDialog={setSuccessDialog} />
        </Box>
      </div>
      <Footer />
    </>
  );
};

export default EducationServiceScreen;